import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const api = axios.create({
  baseURL: API,
});

// Token management
const TOKEN_KEY = 'divispend_access_token';
const REFRESH_KEY = 'divispend_refresh_token';

export function saveTokens(access, refresh) {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

// Attach token to every request via both Authorization and custom header
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-Access-Token'] = token;
  }
  return config;
});

// Auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this IS the refresh call or the login/register call
      const url = originalRequest.url || '';
      if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          if (data.access_token) {
            saveTokens(data.access_token, null);
            processQueue(null, data.access_token);
            originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
            isRefreshing = false;
            return api(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearTokens();
          isRefreshing = false;
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // No refresh token available
      isRefreshing = false;
      clearTokens();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Helper: extract error message from response
export function formatApiError(err) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return err.response?.data?.message || err.message || 'Something went wrong';
}

// Helper: resolve image URLs (relative paths from backend to absolute)
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
export function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_BASE}${url}`;
}

// ========================= API MODULES =========================

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => { clearTokens(); return api.post('/auth/logout'); },
  getMe: () => api.get('/auth/me'),
  refresh: () => {
    const refreshToken = getRefreshToken();
    return api.post('/auth/refresh', { refresh_token: refreshToken });
  },
  googleSession: (sessionId) => api.post('/auth/google-session', { session_id: sessionId }),
};

// Users APIs
export const usersAPI = {
  search: (query) => api.get(`/users/search?q=${encodeURIComponent(query)}`),
  getProfile: (userId) => api.get(`/users/profile/${userId}`),
  getByUsername: (username) => api.get(`/users/by-username/${username}`),
  getStorefront: (username) => api.get(`/users/storefront/${username}`),
  updateProfile: (data) => api.put('/users/me', data),
};

// Products APIs
export const productsAPI = {
  getAll: (category) => api.get(`/products${category ? `?category=${encodeURIComponent(category)}` : ''}`),
  getMy: () => api.get('/products/my'),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  contribute: (id, amount, message) => api.post(`/products/${id}/contribute`, { amount, message }),
  getBackers: (id) => api.get(`/products/${id}/backers`),
  // Aliases used by pages
  get: (id) => api.get(`/products/${id}`),
  myProducts: () => api.get('/products/my'),
  list: (category) => api.get(`/products${category ? `?category=${encodeURIComponent(category)}` : ''}`),
};

// Transactions APIs
export const transactionsAPI = {
  send: (data) => api.post('/transactions/send', data),
  getHistory: (limit = 20) => api.get(`/transactions/history?limit=${limit}`),
};

// Payment Requests APIs
export const requestsAPI = {
  create: (data) => api.post('/requests', data),
  getIncoming: () => api.get('/requests/incoming'),
  getOutgoing: () => api.get('/requests/outgoing'),
  accept: (id) => api.post(`/requests/${id}/accept`),
  reject: (id) => api.post(`/requests/${id}/reject`),
};

// Wallet APIs
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  topup: (amount, originUrl, paymentMethod) => api.post('/wallet/topup', { amount, origin_url: originUrl, payment_method: paymentMethod || 'stripe' }),
  topupStatus: (sessionId) => api.get(`/wallet/topup/status/${sessionId}`),
  paypalCapture: (orderId) => api.post(`/wallet/paypal/capture/${orderId}`),
};

// Payments APIs
export const paymentsAPI = {
  productCheckout: (data) => api.post('/payments/product/checkout', data),
  checkStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  paypalCapture: (orderId) => api.post(`/payments/paypal/capture/${orderId}`),
  checkoutProduct: (productId, originUrl) => api.post('/payments/product/checkout', { product_id: productId, origin_url: originUrl }),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Social APIs
export const followsAPI = {
  follow: (userId) => api.post(`/follows/${userId}`),
  unfollow: (userId) => api.delete(`/follows/${userId}`),
  getFollowers: () => api.get('/follows/followers'),
  getFollowing: () => api.get('/follows/following'),
};

export const feedAPI = {
  getFeed: (limit = 20) => api.get(`/feed?limit=${limit}`),
};

// Favorites APIs
export const favoritesAPI = {
  getAll: () => api.get('/favorites'),
  addUser: (userId) => api.post(`/favorites/user/${userId}`),
  addProduct: (productId) => api.post(`/favorites/product/${productId}`),
  remove: (favoriteId) => api.delete(`/favorites/${favoriteId}`),
  list: () => api.get('/favorites'),
  favoriteProduct: (productId) => api.post(`/favorites/product/${productId}`),
};

// Uploads
export const uploadsAPI = {
  uploadProductImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/product-image', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
  },
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
  },
  uploadDigitalFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/downloads/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
  },
};

// QR APIs
export const qrAPI = {
  recordScan: (type, targetId) => api.post(`/qr/scan/${type}/${targetId}`),
  getStats: (type, targetId) => api.get(`/qr/stats/${type}/${targetId}`),
  getMyStats: () => api.get('/qr/my-stats'),
};

// Pay Links APIs
export const payLinksAPI = {
  resolve: (username) => api.get(`/pay-links/resolve/${username}`),
  send: (data) => api.post('/pay-links/send', data),
  getStats: () => api.get('/pay-links/stats'),
};

// Embed APIs
export const embedAPI = {
  getProducts: (username, theme, limit) =>
    api.get(`/embed/products/${username}?theme=${theme || 'dark'}&limit=${limit || 6}`),
  getStats: () => api.get('/embed/stats'),
};

// Fees APIs
export const feesAPI = {
  calculate: (amount) => api.get(`/fees/calculate?amount=${amount}`),
  getSettings: () => api.get('/fees/settings'),
};

// Receipts APIs
export const receiptsAPI = {
  get: (transactionId) => api.get(`/receipts/${transactionId}`),
  getPdfUrl: (transactionId) => `${API}/receipts/${transactionId}/pdf`,
};

// Notifications APIs
export const notificationsAPI = {
  getAll: (limit = 20, unreadOnly = false) => api.get(`/notifications?limit=${limit}&unread_only=${unreadOnly}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Admin APIs
export const adminAPI = {
  getFees: () => api.get('/admin/fees'),
  updateFees: (data) => api.put('/admin/fees', data),
  getAnalytics: () => api.get('/admin/analytics'),
  getStorage: () => api.get('/admin/storage'),
  updateStorage: (data) => api.put('/admin/storage', data),
  getPlatformStats: () => api.get('/admin/platform-stats'),
  // Admin user management (also available via adminUsersAPI)
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  getUserDetail: (userId) => api.get(`/admin/users/${userId}`),
  banUser: (userId) => api.put(`/admin/users/${userId}/ban`),
  unbanUser: (userId) => api.put(`/admin/users/${userId}/unban`),
  changeRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  adjustBalance: (userId, amount, reason) => api.put(`/admin/users/${userId}/balance`, { amount, reason }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  reconcilePreview: () => api.get('/admin/reconcile-balances'),
  reconcileApply: () => api.post('/admin/reconcile-balances/apply'),
};

// Admin Users APIs
export const adminUsersAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  banUser: (userId) => api.put(`/admin/users/${userId}/ban`),
  unbanUser: (userId) => api.put(`/admin/users/${userId}/unban`),
  changeRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  adjustBalance: (userId, amount, reason) => api.put(`/admin/users/${userId}/balance`, { amount, reason }),
};

// Seller Analytics APIs
export const sellerAnalyticsAPI = {
  getOverview: () => api.get('/seller-analytics/overview'),
  getRevenueChart: (period = '30d') => api.get(`/seller-analytics/revenue-chart?period=${period}`),
  getTopProducts: (limit = 10) => api.get(`/seller-analytics/top-products?limit=${limit}`),
  getCustomers: (limit = 20) => api.get(`/seller-analytics/customers?limit=${limit}`),
  overview: () => api.get('/seller-analytics/overview'),
  revenueChart: (period = '30d') => api.get(`/seller-analytics/revenue-chart?period=${period}`),
  topProducts: (limit = 10) => api.get(`/seller-analytics/top-products?limit=${limit}`),
  customers: (limit = 20) => api.get(`/seller-analytics/customers?limit=${limit}`),
};

// CRM APIs
export const crmAPI = {
  getContacts: (params) => api.get('/crm/contacts', { params }),
  updateNotes: (contactId, text, tags) => api.put(`/crm/contacts/${contactId}/notes`, { text, tags }),
  exportCsv: () => `${API}/crm/contacts/export`,
  getSegments: () => api.get('/crm/segments'),
  createSegment: (data) => api.post('/crm/segments', data),
  updateSegment: (id, data) => api.put(`/crm/segments/${id}`, data),
  deleteSegment: (id) => api.delete(`/crm/segments/${id}`),
  getThreads: (limit = 30) => api.get(`/crm/threads?limit=${limit}`),
  createThread: (buyerId, subject, message) => api.post('/crm/threads', { buyer_id: buyerId, subject, message }),
  getThread: (threadId) => api.get(`/crm/threads/${threadId}`),
  replyToThread: (threadId, message) => api.post(`/crm/threads/${threadId}/reply`, { message }),
  broadcast: (subject, message, segmentId) => api.post('/crm/broadcast', { subject, message, segment_id: segmentId }),
  getFollowup: () => api.get('/crm/auto-followup'),
  updateFollowup: (data) => api.put('/crm/auto-followup', data),
};

// Buyer Messages APIs
export const buyerMessagesAPI = {
  getThreads: (limit = 30) => api.get(`/buyer-messages/threads?limit=${limit}`),
  getThread: (threadId) => api.get(`/buyer-messages/threads/${threadId}`),
  reply: (threadId, message) => api.post(`/buyer-messages/threads/${threadId}/reply`, { message }),
};

// Admin Domains APIs
export const adminDomainsAPI = {
  list: () => api.get('/admin/domains'),
  get: (domainId) => api.get(`/admin/domains/${domainId}`),
  create: (domain) => api.post('/admin/domains', { domain }),
  verify: (domainId) => api.post(`/admin/domains/${domainId}/verify`),
};

// Admin API Docs
export const adminApiDocsAPI = {
  getCatalog: () => api.get('/admin/api-docs'),
};

// Reviews APIs
export const reviewsAPI = {
  create: (product_id, rating, text) => api.post('/reviews', { product_id, rating, text }),
  getForProduct: (productId) => api.get(`/reviews/product/${productId}`),
  getForSeller: (userId) => api.get(`/reviews/seller/${userId}`),
  delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
};

// Push Notification APIs
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.delete('/push/subscribe', { data: { endpoint } }),
};

// Withdrawals APIs
export const withdrawalsAPI = {
  request: (data) => api.post('/withdrawals/request', data),
  getMy: () => api.get('/withdrawals/my'),
  getAll: (status) => api.get(`/withdrawals/all${status ? `?status=${status}` : ''}`),
  approve: (id, adminNote) => api.put(`/withdrawals/${id}/approve`, { admin_note: adminNote }),
  reject: (id, adminNote) => api.put(`/withdrawals/${id}/reject`, { admin_note: adminNote }),
};

// 2FA APIs
export const twofaAPI = {
  setup: () => api.post('/2fa/setup'),
  getQrUrl: () => `${API}/2fa/setup/qr`,
  enable: (code) => api.post('/2fa/enable', { code }),
  disable: (code) => api.post('/2fa/disable', { code }),
  status: () => api.get('/2fa/status'),
  regenerateBackup: (code) => api.post('/2fa/regenerate-backup', { code }),
};

// Audit Log APIs
export const auditAPI = {
  getLogs: (params) => api.get('/audit', { params }),
  getActions: () => api.get('/audit/actions'),
  getChain: (params) => api.get('/audit/chain', { params }),
  verifyChain: (limit = 10000) => api.get('/audit/chain/verify', { params: { limit } }),
};

// Advanced Analytics APIs
export const advancedAnalyticsAPI = {
  getFunnel: () => api.get('/analytics/funnel'),
  getCohorts: (period) => api.get(`/analytics/cohorts?period=${period || 'monthly'}`),
  getRevenueForecast: () => api.get('/analytics/revenue-forecast'),
  getKPIs: () => api.get('/analytics/kpis'),
};

// Fraud Detection APIs
export const fraudAPI = {
  getAlerts: (params) => api.get('/fraud/alerts', { params }),
  dismissAlert: (id) => api.put(`/fraud/alerts/${id}/dismiss`),
  investigateAlert: (id) => api.put(`/fraud/alerts/${id}/investigate`),
  getThresholds: () => api.get('/fraud/thresholds'),
  updateThresholds: (data) => api.put('/fraud/thresholds', data),
  getStats: () => api.get('/fraud/stats'),
};

// GDPR APIs
export const gdprAPI = {
  exportData: () => api.get('/gdpr/export'),
  requestDeletion: () => api.post('/gdpr/delete-request'),
  cancelDeletion: () => api.post('/gdpr/cancel-deletion'),
  getDeletionStatus: () => api.get('/gdpr/deletion-status'),
  getConsent: () => api.get('/gdpr/consent'),
  updateConsent: (data) => api.put('/gdpr/consent', data),
};

// Referrals APIs
export const referralsAPI = {
  getMyCode: () => api.get('/referrals/my-code'),
  sendInviteEmail: (emails) => api.post('/referrals/invite-email', { emails }),
  getTracking: () => api.get('/referrals/tracking'),
};

// Feedback APIs
export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
  getAll: (params) => api.get('/feedback', { params }),
  getStats: () => api.get('/feedback/stats'),
  updateStatus: (id, status, adminNote) => api.put(`/feedback/${id}/status`, { status, admin_note: adminNote }),
  remove: (id) => api.delete(`/feedback/${id}`),
};

// Investments APIs
export const investmentsAPI = {
  invest: (productId) => api.post(`/investments/${productId}/invest`),
  getMy: () => api.get('/investments/my'),
  getProductInvestors: (productId) => api.get(`/investments/product/${productId}`),
  getUserInvestments: (userId) => api.get(`/investments/user/${userId}`),
};

// Offers APIs
export const offersAPI = {
  makeOffer: (productId, amount, message) => api.post(`/offers/${productId}`, { amount, message }),
  getIncoming: () => api.get('/offers/incoming'),
  getMy: () => api.get('/offers/my'),
  getProductOffers: (productId) => api.get(`/offers/product/${productId}`),
  accept: (offerId) => api.put(`/offers/${offerId}/accept`),
  decline: (offerId) => api.put(`/offers/${offerId}/decline`),
};

// Subscriptions APIs
export const subscriptionsAPI = {
  subscribe: (productId, paymentMethod, originUrl) => api.post(`/subscriptions/${productId}/subscribe`, { payment_method: paymentMethod, origin_url: originUrl }),
  confirmStripe: (sessionId) => api.post(`/subscriptions/stripe/confirm/${sessionId}`),
  getMy: () => api.get('/subscriptions/my'),
  getSubscribers: () => api.get('/subscriptions/subscribers'),
  cancel: (subId) => api.post(`/subscriptions/${subId}/cancel`),
};

// Investor Dashboard APIs
export const investorDashboardAPI = {
  getOverview: () => api.get('/investor-dashboard/overview'),
  getReferralStats: (productId) => api.get(`/investor-dashboard/referral-stats/${productId}`),
};

// My Library / Digital Downloads (buyer-side)
export const downloadsAPI = {
  getMyLibrary: () => api.get('/downloads/my-library'),
  getDownloadUrl: (purchaseId) => api.get(`/downloads/file/${purchaseId}`),
};

// Email verification, forgot password, passcode (app-lock)
export const authExtrasAPI = {
  sendVerificationEmail: (email) => api.post('/auth/send-verification-email', { email }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  completeForcedReset: (user_id, reset_ticket, new_password) => api.post('/auth/complete-forced-reset', { user_id, reset_ticket, new_password }),
  passcodeStatus: () => api.get('/auth/passcode/status'),
  setPasscode: (passcode) => api.post('/auth/passcode/set', { passcode }),
  changePasscode: (current_passcode, new_passcode) => api.post('/auth/passcode/change', { current_passcode, new_passcode }),
  verifyPasscode: (passcode) => api.post('/auth/passcode/verify', { passcode }),
  disablePasscode: () => api.delete('/auth/passcode'),
};

// Feature Flags
export const featureFlagsAPI = {
  getVisible: () => api.get('/feature-flags/visible'),
  getRoadmap: () => api.get('/feature-flags/roadmap'),
  adminListAll: () => api.get('/feature-flags/admin/all'),
  adminUpdate: (key, data) => api.patch(`/feature-flags/admin/${key}`, data),
  adminLaunchPhase: (phase, enabled = true, beta_only = false) =>
    api.post('/feature-flags/admin/launch-phase', { phase, enabled, beta_only }),
  adminSeed: () => api.post('/feature-flags/admin/seed'),
};

// Beta access
export const betaAPI = {
  status: () => api.get('/beta/status'),
  redeem: (code) => api.post('/beta/redeem', { code }),
  adminListUsers: () => api.get('/beta/admin/users'),
  adminToggleUser: (user_id, is_beta) => api.patch(`/beta/admin/users/${user_id}`, { is_beta }),
  adminListCodes: () => api.get('/beta/admin/codes'),
  adminCreateCode: (data) => api.post('/beta/admin/codes', data),
  adminDeleteCode: (code) => api.delete(`/beta/admin/codes/${code}`),
};

// Crowdfund
export const crowdfundAPI = {
  getCampaign: () => api.get('/crowdfund/campaign'),
  getBackers: (limit = 50) => api.get(`/crowdfund/backers?limit=${limit}`),
  pledge: (tier_key, origin_url, display_name) =>
    api.post('/crowdfund/pledge', { tier_key, origin_url, display_name }),
  pledgeStatus: (pledge_id) => api.get(`/crowdfund/pledge/${pledge_id}/status`),
  myPledges: () => api.get('/crowdfund/my-pledges'),
  adminOverview: () => api.get('/crowdfund/admin/overview'),
  adminUpdateCampaign: (data) => api.patch('/crowdfund/admin/campaign', data),
};

// Waitlist
export const waitlistAPI = {
  join: (data) => api.post('/waitlist', data),
  adminList: (params = {}) => api.get('/waitlist/admin/entries', { params }),
  adminUpdate: (entryId, status) => api.patch(`/waitlist/admin/${entryId}`, { status }),
  adminAddLead: (data) => api.post('/waitlist/admin/add', data),
  adminExport: (params = {}) => api.get('/waitlist/admin/export', { params, responseType: 'blob' }),
};

// Unified Dashboard
export const unifiedAPI = {
  overview: () => api.get('/unified/overview'),
  linkToken: () => api.post('/unified/plaid/link-token'),
  exchange: (publicToken, institutionName) => api.post('/unified/plaid/exchange', { public_token: publicToken, institution_name: institutionName }),
  classify: (accountId, subtype) => api.patch(`/unified/accounts/${accountId}`, { account_subtype: subtype }),
  removeItem: (itemId) => api.delete(`/unified/items/${itemId}`),
  syncAll: () => api.post('/unified/sync'),
  fixCategory: (txId, category) => api.patch(`/unified/transactions/${txId}`, { category }),
  receiptOcr: (imageBase64) => api.post('/unified/receipts/ocr', { image_base64: imageBase64 }),
  receiptConfirm: (draft) => api.post('/unified/receipts/confirm', { receipt_id: draft.receipt_id, vendor: draft.vendor, amount: parseFloat(draft.amount), date: draft.date, category: draft.category }),
  receiptOcrManual: (draft) => api.post('/unified/receipts/manual', { vendor: draft.vendor, amount: parseFloat(draft.amount), date: draft.date, category: draft.category }),
  reilTrack: (status) => api.post('/unified/financing/reil', { status }),
};

// Business Suite — LEGACY invite-redemption pipeline only (predates the
// Supabase/org model, mints a legacy consumer-style JWT). Everything else
// that talks to /api/biz/* now lives in services/bizApi.js and is
// authenticated exclusively via Supabase (see BusinessAuthContext).
export const bizAPI = {
  redeem: (data) => api.post('/biz/redeem', data),
  config: () => api.get('/biz/config'),
};

export const pledgeAPI = {
  getPage: (id) => api.get(`/pledge/${id}`),
  checkout: (id, amount, originUrl) => api.post(`/pledge/${id}/checkout`, { amount, origin_url: originUrl }),
  sessionStatus: (sid) => api.get(`/pledge/session/${sid}/status`),
};
